/**
 * GRABACIÓN EN CURSO, ESCRITA MIENTRAS SUCEDE.
 *
 * El agujero que cierra: `MediaRecorder` acumula todo en la memoria de la
 * pestaña y solo entrega el audio al DETENER. Una grabación de sala de dos
 * horas no existe en ningún lado hasta que alguien aprieta «Detener»: si la
 * laptop se suspende, el navegador mata la pestaña por memoria o se corta la
 * luz, se pierde la jornada entera. La cola durable (`colaMedia`) no ayuda
 * ahí — nunca llegó a existir un blob que encolar.
 *
 * La solución es grabar por TROZOS (`MediaRecorder.start(timeslice)`) y
 * escribir cada uno a IndexedDB apenas llega. Al reabrir la app, los trozos
 * huérfanos se reensamblan en un audio completo. Se pierde, como mucho, el
 * último trozo (segundos), nunca la sesión.
 *
 * Los trozos son piezas de UN contenedor (webm/mp4): solo valen concatenados
 * en orden y desde el primero. Por eso la clave es [grabacionId, indice] y
 * nunca se borra un trozo suelto.
 */
import {
  ALMACEN_GRABACIONES,
  ALMACEN_TROZOS,
  conAlmacen,
} from './db';

/** Qué se está grabando: define cómo se re-entrega si hay que recuperarla. */
export type ContextoGrabacion = 'nota' | 'sala';

/** Cabecera de una grabación en curso (o interrumpida). */
export interface GrabacionEnCurso {
  id: string;
  contexto: ContextoGrabacion;
  actividadId: string;
  mime: string;
  ext: string;
  /** ISO del arranque real de la grabación. */
  inicio: string;
  /** ISO del último trozo escrito: el «fin» si la grabación se interrumpió. */
  ultimoTrozo: string;
  /** Trozos escritos hasta ahora (para mostrar la duración aproximada). */
  trozos: number;
  /** Segundos que representa cada trozo (para estimar la duración). */
  segundosPorTrozo: number;
  microfonoSala?: boolean;
  numHablantes?: number;
  campos?: Record<string, unknown>;
  /** Marca de cierre limpio: una grabación cerrada NO es huérfana. */
  cerrada?: boolean;
}

interface TrozoGuardado {
  grabacionId: string;
  indice: number;
  blob: Blob;
  ts: string;
}

/** Abre el expediente de una grabación antes del primer trozo. */
export async function abrirGrabacion(meta: GrabacionEnCurso): Promise<void> {
  await conAlmacen(ALMACEN_GRABACIONES, 'readwrite', (s) => s.put(meta) as IDBRequest<IDBValidKey>);
}

/** Escribe un trozo. Se llama cada `timeslice` mientras se graba. */
export async function guardarTrozo(
  grabacionId: string,
  indice: number,
  blob: Blob,
): Promise<void> {
  const trozo: TrozoGuardado = {
    grabacionId,
    indice,
    blob,
    ts: new Date().toISOString(),
  };
  await conAlmacen(ALMACEN_TROZOS, 'readwrite', (s) => s.put(trozo) as IDBRequest<IDBValidKey>);
  // La cabecera sigue al día: si la pestaña muere ahora, sabemos cuántos
  // trozos hay y hasta qué hora llegó la grabación.
  const meta = await leerCabecera(grabacionId);
  if (meta) {
    await abrirGrabacion({ ...meta, trozos: indice + 1, ultimoTrozo: trozo.ts });
  }
}

async function leerCabecera(id: string): Promise<GrabacionEnCurso | undefined> {
  return conAlmacen<GrabacionEnCurso | undefined>(ALMACEN_GRABACIONES, 'readonly', (s) =>
    s.get(id),
  );
}

/** Todos los trozos de una grabación, EN ORDEN. */
async function leerTrozos(id: string): Promise<Blob[]> {
  const rango = IDBKeyRange.bound([id, 0], [id, Number.MAX_SAFE_INTEGER]);
  const filas = await conAlmacen<TrozoGuardado[]>(ALMACEN_TROZOS, 'readonly', (s) =>
    s.getAll(rango),
  );
  return filas.sort((a, b) => a.indice - b.indice).map((f) => f.blob);
}

/** Reensambla la grabación completa a partir de sus trozos. */
export async function reensamblar(id: string): Promise<Blob | null> {
  const meta = await leerCabecera(id);
  if (!meta) return null;
  const trozos = await leerTrozos(id);
  if (trozos.length === 0) return null;
  return new Blob(trozos, { type: meta.mime || 'audio/webm' });
}

/** Borra la grabación y todos sus trozos (ya se entregó, o se descartó). */
export async function borrarGrabacion(id: string): Promise<void> {
  const rango = IDBKeyRange.bound([id, 0], [id, Number.MAX_SAFE_INTEGER]);
  await conAlmacen(ALMACEN_TROZOS, 'readwrite', (s) => s.delete(rango) as unknown as IDBRequest<undefined>);
  await conAlmacen(ALMACEN_GRABACIONES, 'readwrite', (s) => s.delete(id) as unknown as IDBRequest<undefined>);
}

/** Marca la grabación como cerrada limpiamente (deja de ser huérfana). */
export async function marcarCerrada(id: string): Promise<void> {
  const meta = await leerCabecera(id);
  if (meta) await abrirGrabacion({ ...meta, cerrada: true });
}

/**
 * Grabaciones que NUNCA se cerraron: la pestaña murió, la laptop se suspendió
 * o se cortó la luz mientras grababan. Son exactamente lo que antes se perdía.
 */
export async function listarHuerfanas(): Promise<GrabacionEnCurso[]> {
  const todas = await conAlmacen<GrabacionEnCurso[]>(ALMACEN_GRABACIONES, 'readonly', (s) =>
    s.getAll(),
  );
  return todas
    .filter((g) => !g.cerrada && g.trozos > 0)
    .sort((a, b) => (a.inicio < b.inicio ? -1 : 1));
}

/** Duración aproximada de una grabación por su número de trozos. */
export function duracionAproximada(g: GrabacionEnCurso): number {
  return Math.max(1, Math.round(g.trozos * g.segundosPorTrozo));
}
